import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Send, MessageSquare, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function Messages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const markRead = useCallback(async () => {
    try {
      await api.post('/messages/read');
    } catch (error) {
      // Non-critical
    }
  }, []);

  const fetchMessages = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await api.get('/messages');
      const incoming = response.data.messages || [];
      const unreadCount = incoming.filter((m) => m.sender_role === 'admin' && !m.read_by_user).length;
      setUnread(unreadCount);
      if (unreadCount > 0) {
        await markRead();
        setUnread(0);
        setMessages(incoming.map((m) => (m.sender_role === 'admin' ? { ...m, read_by_user: true } : m)));
      } else {
        setMessages(incoming);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      if (showSpinner) toast.error('Failed to load messages');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [markRead]);

  useEffect(() => {
    fetchMessages(true);
    const interval = setInterval(() => {
      fetchMessages(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    try {
      setSending(true);
      const response = await api.post('/messages', { body: text });
      setMessages((prev) => [...prev, response.data]);
      setBody('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastPreview = lastMessage
    ? `${lastMessage.sender_role === 'user' ? 'You: ' : ''}${lastMessage.body}`
    : 'Start a conversation with our team';

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl">
      <div className="flex items-center gap-3 mb-8">
        <MessageSquare className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Messages</h1>
          <p className="text-muted-foreground">Send a message to our team and read their replies</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex h-[60vh]">
            {/* Left column: recipients */}
            <div className="hidden md:flex w-64 border-r flex-col shrink-0">
              <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium text-muted-foreground">
                Conversations
              </div>
              <div className="flex-1 overflow-y-auto">
                <button
                  className="w-full text-left px-3 py-3 flex items-center gap-3 bg-muted"
                  type="button"
                >
                  <div className="relative shrink-0">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="h-4 w-4 text-primary" />
                    </div>
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-background" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm truncate ${unread > 0 ? 'font-semibold' : 'font-medium'}`}>
                        RecordRinse Support
                      </p>
                      {unread > 0 && (
                        <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-semibold shrink-0">
                          {unread}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs truncate ${unread > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {lastPreview}
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Right column: conversation thread */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center gap-3 px-4 py-3 border-b">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Shield className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">RecordRinse Support</p>
                  <p className="text-xs text-muted-foreground truncate">We usually reply within a day</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                    <MessageSquare className="h-10 w-10 mb-3 opacity-40" />
                    <p className="font-medium">No messages yet</p>
                    <p className="text-sm">Send your first message below and our team will get back to you.</p>
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_role === 'user';
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                          {!mine && (
                            <p className="text-xs font-semibold mb-0.5 opacity-80">
                              {m.sender_name || 'Support'}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                            {formatTime(m.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={handleSend} className="flex gap-2 items-end border-t p-4">
                <Textarea
                  placeholder="Type your message..."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  rows={2}
                  className="resize-none"
                  disabled={sending}
                />
                <Button type="submit" disabled={sending || !body.trim()}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
